import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { en } from '../../translations/en';
import { logger } from '../../utils/logger';

interface DlqStatus {
  dlq: {
    url: string;
    messages_available: number;
    messages_in_flight: number;
    messages_delayed: number;
  };
  main_queue: {
    url: string;
    messages_available: number;
    messages_in_flight: number;
  } | null;
  timestamp: string;
}

interface StuckItem {
  catalogItemId: string;
  userId: string;
  status: string;
  images: { imageId: string; filename: string }[];
  createdAt: string;
  updatedAt: string;
}

interface StuckItemsResponse {
  count: number;
  items: StuckItem[];
  min_age_minutes: number;
  timestamp: string;
}

interface FailedItem {
  catalogItemId: string;
  userId: string;
  status: string;
  errorMessage: string;
  images: { imageId: string; filename: string; status: string }[];
  createdAt: string;
  updatedAt: string;
}

interface FailedItemsResponse {
  count: number;
  items: FailedItem[];
  timestamp: string;
}

interface RetryTarget {
  userId: string;
  catalogItemId: string;
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const ProcessingTab: React.FC = () => {
  const [dlqStatus, setDlqStatus] = useState<DlqStatus | null>(null);
  const [dlqLoading, setDlqLoading] = useState(false);
  const [dlqError, setDlqError] = useState<string | null>(null);
  const [stuckItems, setStuckItems] = useState<StuckItemsResponse | null>(null);
  const [stuckLoading, setStuckLoading] = useState(false);
  const [redriving, setRedriving] = useState(false);
  const [redriveSuccess, setRedriveSuccess] = useState<string | null>(null);
  const [redriveError, setRedriveError] = useState<string | null>(null);
  const [redriveConfirmDialogOpen, setRedriveConfirmDialogOpen] = useState(false);

  const [failedItems, setFailedItems] = useState<FailedItemsResponse | null>(null);
  const [failedLoading, setFailedLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retrySuccess, setRetrySuccess] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retryConfirmDialogOpen, setRetryConfirmDialogOpen] = useState(false);
  const [retryTargetItems, setRetryTargetItems] = useState<RetryTarget[]>([]);

  const fetchDlqStatus = useCallback(async () => {
    setDlqLoading(true);
    setDlqError(null);
    try {
      const response = await api.get('/api/admin/processing/dlq/status');
      setDlqStatus(response.data);
    } catch (_err: unknown) {
      setDlqError(en.admin.processing.queueNotConfigured);
    } finally {
      setDlqLoading(false);
    }
  }, []);

  const fetchStuckItems = useCallback(async () => {
    setStuckLoading(true);
    try {
      const response = await api.get('/api/admin/processing/stuck?minAgeMinutes=30');
      setStuckItems(response.data);
    } catch (error) {
      logger.warn('Failed to load stuck processing items', error);
    } finally {
      setStuckLoading(false);
    }
  }, []);

  const fetchFailedItems = useCallback(async () => {
    setFailedLoading(true);
    try {
      const response = await api.get('/api/admin/processing/failed');
      setFailedItems(response.data);
    } catch (error) {
      logger.warn('Failed to load failed processing items', error);
    } finally {
      setFailedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDlqStatus();
    fetchStuckItems();
    fetchFailedItems();
  }, [fetchDlqStatus, fetchStuckItems, fetchFailedItems]);

  const handleConfirmRedrive = async () => {
    setRedriveConfirmDialogOpen(false);
    setRedriving(true);
    setRedriveSuccess(null);
    setRedriveError(null);

    try {
      const response = await api.post('/api/admin/processing/dlq/redrive?maxMessages=100');
      setRedriveSuccess(`${en.admin.processing.redriveSuccess}: ${response.data.redriven} messages`);
      fetchDlqStatus();
      fetchStuckItems();
      fetchFailedItems();
    } catch (_err: unknown) {
      setRedriveError(en.admin.processing.redriveError);
    } finally {
      setRedriving(false);
    }
  };

  const openRetryConfirm = (items: RetryTarget[]) => {
    setRetryTargetItems(items);
    setRetryConfirmDialogOpen(true);
  };

  const handleConfirmRetry = async () => {
    setRetryConfirmDialogOpen(false);
    setRetrying(true);
    setRetrySuccess(null);
    setRetryError(null);

    // Backend caps RetryItemsRequest.items at 50 (backend/src/routers/admin.py:81).
    // Send sequential batches so the FastAPI Pydantic validator never rejects
    // the request, and so concurrent backend load matches the single-batch shape.
    const BATCH_SIZE = 50;
    const totals = { images_queued: 0, skipped: 0, errors: 0 };

    try {
      for (let i = 0; i < retryTargetItems.length; i += BATCH_SIZE) {
        const batch = retryTargetItems.slice(i, i + BATCH_SIZE);
        const response = await api.post('/api/admin/processing/retry', { items: batch });
        const { images_queued, skipped, errors } = response.data;
        totals.images_queued += images_queued ?? 0;
        totals.skipped += skipped ?? 0;
        totals.errors += errors?.length ?? 0;
      }

      let message = `${en.admin.processing.retrySuccess}: ${totals.images_queued} images queued`;
      if (totals.skipped > 0) message += `, ${totals.skipped} items skipped (no eligible images)`;
      if (totals.errors > 0) message += ` (${totals.errors} errors)`;
      setRetrySuccess(message);
      fetchFailedItems();
      fetchStuckItems();
    } catch (_err: unknown) {
      setRetryError(en.admin.processing.retryError);
    } finally {
      setRetrying(false);
      setRetryTargetItems([]);
    }
  };

  const refreshAll = () => {
    fetchDlqStatus();
    fetchStuckItems();
    fetchFailedItems();
  };

  const isAnyLoading = dlqLoading || stuckLoading || failedLoading;

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* DLQ Status Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">{en.admin.processing.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {en.admin.processing.description}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshAll}
              disabled={isAnyLoading}
            >
              {en.admin.processing.refresh}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setRedriveConfirmDialogOpen(true)}
              disabled={redriving || !dlqStatus || dlqStatus.dlq.messages_available === 0}
              startIcon={redriving ? <CircularProgress size={20} color="inherit" /> : <ReplayIcon />}
            >
              {redriving ? en.admin.processing.redriving : en.admin.processing.redrive}
            </Button>
          </Box>
        </Box>

        {redriveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRedriveSuccess(null)}>
            {redriveSuccess}
          </Alert>
        )}

        {redriveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRedriveError(null)}>
            {redriveError}
          </Alert>
        )}

        {retrySuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRetrySuccess(null)}>
            {retrySuccess}
          </Alert>
        )}

        {retryError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRetryError(null)}>
            {retryError}
          </Alert>
        )}

        {dlqError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDlqError(null)}>
            {dlqError}
          </Alert>
        )}

        {dlqLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {dlqStatus && !dlqLoading && (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* DLQ Status */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {en.admin.processing.dlqTitle}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {en.admin.processing.messagesAvailable}
                      </Typography>
                      <Chip
                        label={dlqStatus.dlq.messages_available}
                        size="small"
                        color={dlqStatus.dlq.messages_available > 0 ? 'error' : 'success'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {en.admin.processing.messagesInFlight}
                      </Typography>
                      <Typography variant="body2">
                        {dlqStatus.dlq.messages_in_flight}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        {en.admin.processing.messagesDelayed}
                      </Typography>
                      <Typography variant="body2">
                        {dlqStatus.dlq.messages_delayed}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Main Queue Status */}
              {dlqStatus.main_queue && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {en.admin.processing.mainQueueTitle}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {en.admin.processing.messagesAvailable}
                        </Typography>
                        <Chip
                          label={dlqStatus.main_queue.messages_available}
                          size="small"
                          color={dlqStatus.main_queue.messages_available > 10 ? 'warning' : 'success'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          {en.admin.processing.messagesInFlight}
                        </Typography>
                        <Typography variant="body2">
                          {dlqStatus.main_queue.messages_in_flight}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Stuck Items Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {en.admin.processing.stuckItemsTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {en.admin.processing.stuckItemsDescription}
              </Typography>
            </Box>

            {stuckLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {stuckItems && !stuckLoading && (
              stuckItems.count === 0 ? (
                <Alert severity="success">
                  {en.admin.processing.noStuckItems}
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {stuckItems.items.map((item) => (
                    <Grid item xs={12} md={6} key={item.catalogItemId}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {item.catalogItemId}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>User:</strong> {item.userId.substring(0, 8)}...
                          </Typography>
                          <Typography variant="body2">
                            <strong>Images:</strong> {item.images.map(img => img.filename).join(', ')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Created: {formatDate(item.createdAt)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )
            )}

            <Divider sx={{ my: 3 }} />

            {/* Failed Items Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">
                    {en.admin.processing.failedItemsTitle}
                  </Typography>
                  {failedItems && failedItems.count > 0 && (
                    <Chip label={failedItems.count} size="small" color="error" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {en.admin.processing.failedItemsDescription}
                </Typography>
              </Box>
              {failedItems && failedItems.count > 0 && (
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
                  disabled={retrying}
                  onClick={() => openRetryConfirm(
                    failedItems.items.map(item => ({
                      userId: item.userId,
                      catalogItemId: item.catalogItemId,
                    }))
                  )}
                >
                  {retrying ? en.admin.processing.retrying : en.admin.processing.retryAll}
                </Button>
              )}
            </Box>

            {failedLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {failedItems && !failedLoading && (
              failedItems.count === 0 ? (
                <Alert severity="success">
                  {en.admin.processing.noFailedItems}
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {failedItems.items.map((item) => (
                    <Grid item xs={12} md={6} key={item.catalogItemId}>
                      <Card
                        variant="outlined"
                        sx={{ borderLeft: '4px solid', borderLeftColor: 'error.main' }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {item.catalogItemId}
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<ReplayIcon />}
                              disabled={retrying}
                              onClick={() => openRetryConfirm([{
                                userId: item.userId,
                                catalogItemId: item.catalogItemId,
                              }])}
                            >
                              {en.admin.processing.retryItem}
                            </Button>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>User:</strong> {item.userId.substring(0, 8)}...
                          </Typography>
                          <Typography variant="body2">
                            <strong>Images:</strong> {item.images.map(img => img.filename).join(', ')}
                          </Typography>
                          {item.errorMessage && (
                            <Alert
                              severity="error"
                              sx={{ mt: 1, py: 0, '& .MuiAlert-message': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                              title={item.errorMessage}
                            >
                              <Typography variant="body2" noWrap>
                                {item.errorMessage}
                              </Typography>
                            </Alert>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Updated: {formatDate(item.updatedAt)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )
            )}
          </>
        )}
      </Box>

      {/* Redrive Confirmation Dialog */}
      <Dialog
        open={redriveConfirmDialogOpen}
        onClose={() => setRedriveConfirmDialogOpen(false)}
      >
        <DialogTitle>{en.admin.processing.redrive}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {en.admin.processing.confirmRedrive}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedriveConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRedrive} variant="contained" color="primary">
            {en.admin.processing.redrive}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Confirmation Dialog */}
      <Dialog
        open={retryConfirmDialogOpen}
        onClose={() => setRetryConfirmDialogOpen(false)}
      >
        <DialogTitle>
          {retryTargetItems.length === 1 ? en.admin.processing.retryItem : en.admin.processing.retryAll}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {en.admin.processing.confirmRetry}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRetry} variant="contained" color="warning">
            {retrying
              ? en.admin.processing.retrying
              : retryTargetItems.length === 1
                ? en.admin.processing.retryItem
                : en.admin.processing.retryAll}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProcessingTab;
